import type { ReactNode } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    Text,
    Button,
    VStack,
    Box,
    HStack,
    Wrap,
    WrapItem,
} from '@chakra-ui/react';
import { INVOICE_REASON_FLAG_CONFIG, NUMERIC_REASON_BADGE_SCHEME } from '../../components/invoice/invoiceReasonFlagConfig';
import { getNexHttpReasonBadgeCopy } from '../../utils/nexHttpReasonBadge';
import { InvoiceReasonFlagBadge } from '../../components/invoice/InvoiceReasonFlagBadge';
import { useGetSettings } from '../../hooks/useGetSettings';
import { getInvoiceNexIdentifierUi, getNexReconciliationIdentifierModeFromSettings } from '../../utils/invoiceNexIdentifierUi';
import { useIsOverflowing } from '../../hooks/useIsOverflowing';

export interface InvoiceReasonFlagsHelpModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
    <VStack align="stretch" spacing={2}>
        <Text fontSize="sm" fontWeight="semibold" color="gray.800">
            {title}
        </Text>
        <Box fontSize="sm" color="gray.600" lineHeight="tall">
            {children}
        </Box>
    </VStack>
);

/** Inline badge + em dash + sentence fragment (keeps badge aligned with text). */
const BadgeLead = ({ label, colorScheme, children }: { label: string; colorScheme: string; children: ReactNode }) => (
    <HStack align="flex-start" spacing={2} flexWrap="wrap">
        <Box flexShrink={0} pt="2px">
            <InvoiceReasonFlagBadge label={label} colorScheme={colorScheme} />
        </Box>
        <Text as="span" flex="1" minW="200px">
            {children}
        </Text>
    </HStack>
);

const InvoiceReasonFlagsHelpModal = ({ isOpen, onClose }: InvoiceReasonFlagsHelpModalProps) => {
    const c = INVOICE_REASON_FLAG_CONFIG;
    const { data: orgSettings } = useGetSettings();
    const idUi = getInvoiceNexIdentifierUi(getNexReconciliationIdentifierModeFromSettings(orgSettings));
    const [bodyRef, bodyOverflows] = useIsOverflowing(isOpen);

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader
                    fontSize="lg"
                    fontWeight="semibold"
                    color="gray.800"
                    pr={10}
                    borderBottomWidth={bodyOverflows ? '1px' : '0'}
                    borderBottomStyle="solid"
                    borderColor="gray.300"
                >
                    Invoice flag reasons
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody ref={bodyRef} minH={0} pb={2}>
                    <VStack align="stretch" spacing={5}>
                        <Text fontSize="sm" color="gray.600" lineHeight="tall">
                            Badges in the Reason column summarize what Yaxle or NEX flagged on that invoice. Below is
                            what each one means and how it affects submitting to NEX from the invoice detail page.
                        </Text>

                        <Section title="Submit stays available">
                            <Text fontSize="sm" color="gray.600" lineHeight="tall" mb={2}>
                                After review, you can still use Submit for these flags (NEX may apply its own checks).
                            </Text>
                            <Wrap spacing={2} mb={3} align="center">
                                <WrapItem>
                                    <InvoiceReasonFlagBadge label={c.price.label} colorScheme={c.price.colorScheme} />
                                </WrapItem>
                                <WrapItem>
                                    <InvoiceReasonFlagBadge label={c.discount.label} colorScheme={c.discount.colorScheme} />
                                </WrapItem>
                                <WrapItem>
                                    <InvoiceReasonFlagBadge label={c.qty.label} colorScheme={c.qty.colorScheme} />
                                </WrapItem>
                                <WrapItem>
                                    <InvoiceReasonFlagBadge label={c.total.label} colorScheme={c.total.colorScheme} />
                                </WrapItem>
                                <WrapItem>
                                    <InvoiceReasonFlagBadge label={c.vendor.label} colorScheme={c.vendor.colorScheme} />
                                </WrapItem>
                                <WrapItem>
                                    <InvoiceReasonFlagBadge
                                        label={c.specialVendor.label}
                                        colorScheme={c.specialVendor.colorScheme}
                                    />
                                </WrapItem>
                                <WrapItem>
                                    <InvoiceReasonFlagBadge
                                        label={c.locationMismatch.label}
                                        colorScheme={c.locationMismatch.colorScheme}
                                    />
                                </WrapItem>
                                <WrapItem>
                                    <InvoiceReasonFlagBadge
                                        label={c.stale.label}
                                        colorScheme={c.stale.colorScheme}
                                    />
                                </WrapItem>
                            </Wrap>
                            <Text mb={3}>
                                <Text as="span" fontWeight="medium">
                                    Price, discount, quantity, amount, and vendor mismatch
                                </Text>{' '}
                                mean the invoice disagrees with NEX or your expected discount settings on those fields. You can still click Submit after
                                review; many invoices are sent as manually submitted while differences were
                                acknowledged. NEX may respond with its own validation or errors.
                            </Text>
                            <BadgeLead label={c.discount.label} colorScheme={c.discount.colorScheme}>
                                —{' '}
                                <Text as="span" fontWeight="medium">
                                    Discount
                                </Text>{' '}
                                means a line item’s actual discount (list vs buy) is below the expected discount configured
                                for this insurer, vendor, and state in Discount Flag settings.
                            </BadgeLead>
                            <BadgeLead label={c.specialVendor.label} colorScheme={c.specialVendor.colorScheme}>
                                —{' '}
                                <Text as="span" fontWeight="medium">
                                    Special Vendor
                                </Text>{' '}
                                means the supplier text matched a phrase on your organization’s special-vendor list. Yaxle skips auto-submit to NEX so extraction can be verified
                                first; use Submit when you are ready.
                            </BadgeLead>
                            <BadgeLead label={c.locationMismatch.label} colorScheme={c.locationMismatch.colorScheme}>
                                —{' '}
                                <Text as="span" fontWeight="medium">
                                    Shop
                                </Text>{' '}
                                means the extracted RO/PO matches a repair at a different shop than the uploader’s assigned location. Auto-submit is paused so you can confirm the
                                folder; use Submit when you are ready.
                            </BadgeLead>
                            <BadgeLead label={c.stale.label} colorScheme={c.stale.colorScheme}>
                                —{' '}
                                <Text as="span" fontWeight="medium">
                                    Stale
                                </Text>{' '}
                                means the matched NEX folder’s parts are more than one year old. Auto-submit is paused so you can confirm; use Submit when you are ready.
                            </BadgeLead>
                        </Section>

                        <Section title="Must be resolved before submitting">
                            <VStack align="stretch" spacing={3}>
                                <BadgeLead label={idUi.reasonBadgeLabel} colorScheme={c.ro.colorScheme}>
                                    — {idUi.reasonFlagHelpModalMustResolveBody}
                                </BadgeLead>
                                <BadgeLead label={c.math.label} colorScheme={c.math.colorScheme}>
                                    — Line math (quantity × unit price vs. line total), how the invoice total is built
                                    from subtotals and fees, or the scanned total not matching summed lines. For{' '}
                                    <Text as="span" fontWeight="medium">
                                        received
                                    </Text>{' '}
                                    and{' '}
                                    <Text as="span" fontWeight="medium">
                                        credited
                                    </Text>{' '}
                                    invoices, the Submit button stays off until these checks pass.
                                </BadgeLead>
                                <Text>
                                    <Text as="span" fontWeight="medium">
                                        Return slips
                                    </Text>{' '}
                                    do not block Submit on arithmetic alone; you still need {idUi.reasonFlagHelpModalReturnSlipValidPhrase}{' '}
                                    and each returned line matched to NEX before a return slip can go through.
                                </Text>
                            </VStack>
                        </Section>

                        <Section title="Match requirements">
                            <BadgeLead label={c.nex.label} colorScheme={c.nex.colorScheme}>
                                — When a line still needs a matching NEX part, submit is blocked by default until that
                                match is resolved. Your organization&apos;s invoicing setting can allow manual (or
                                manual and automatic) submission with some unmatched billable lines as long as at least
                                one line is matched. This applies to{' '}
                                <Text as="span" fontWeight="medium">
                                    received
                                </Text>{' '}
                                ,{' '}
                                <Text as="span" fontWeight="medium">
                                    credited
                                </Text>{' '}
                                , and{' '}
                                <Text as="span" fontWeight="medium">
                                    return slip
                                </Text>{' '}
                                invoices, including core-charge rows.
                            </BadgeLead>
                        </Section>

                        <Section title="Duplicate or other NEX HTTP errors">
                            <Text fontSize="sm" color="gray.600" lineHeight="tall" mb={2}>
                                These badges reflect a duplicate Yaxle upload (same vendor + invoice number + type
                                already submitted) or Nexsyis’s response to a submit attempt. A credited invoice is not
                                a duplicate of a received invoice with the same number. A Yaxle duplicate can be
                                corrected in-app by changing the invoice number or vendor, then submitting — Submit
                                re-checks the number. A Nexsyis 406 is not a line-reconciliation flag you can clear
                                inside Yaxle; fix the issue in Nexsyis or contact Yaxle if you are stuck.
                            </Text>
                            <VStack align="stretch" spacing={3}>
                                <BadgeLead label={c.dupe.label} colorScheme={c.dupe.colorScheme}>
                                    — Either this invoice number and type were already submitted in Yaxle for the same
                                    resolved vendor (change the invoice number if this is a different document, then
                                    submit), or NEX returned HTTP 406 (usually a duplicate or another business rule).
                                    Retrying a true duplicate with the same data generally will not create a new
                                    invoice. Contact Yaxle if you are unsure how to proceed.
                                </BadgeLead>
                                <HStack align="flex-start" spacing={2} flexWrap="wrap">
                                    <Box flexShrink={0} pt="2px">
                                        <InvoiceReasonFlagBadge
                                            label={getNexHttpReasonBadgeCopy(404).label}
                                            colorScheme={NUMERIC_REASON_BADGE_SCHEME}
                                        />
                                    </Box>
                                    <Text as="span" flex="1" minW="200px">
                                        — Short labels such as this summarize a Nexsyis HTTP error from a failed submit
                                        (hover the badge on the list for details). Open the invoice for the full Nexsyis
                                        message. You cannot reconcile this type of flag in Yaxle—re-upload, correct
                                        Nexsyis, or reach out to Yaxle support.
                                    </Text>
                                </HStack>
                            </VStack>
                        </Section>
                    </VStack>
                </ModalBody>
                <ModalFooter>
                    <Button size="action" variant="outline" onClick={onClose}>Close</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default InvoiceReasonFlagsHelpModal;
